import { useState } from 'react';
import {
  Alert, Anchor, Badge, Box, Button, Checkbox, Container, Divider, Group, List, Paper, PasswordInput, Stack,
  Text, Textarea, TextInput, Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  IconArrowLeft, IconCircleCheck, IconDeviceMobile, IconMail, IconSend, IconShieldLock, IconTrash, IconUserX,
} from '@tabler/icons-react';
import api from '../services/api';
import { LanguageMenu } from '../components/LanguageMenu';
import { useAuth } from '../contexts/AuthContext';
import { APP_NAME, DEVELOPER_NAME, DEVELOPER_URL, PRIVACY_CONTACT_EMAIL } from '../lib/brand';

/** Sabab uzunligi — backend `lib/akkauntOchirish.ts` SABAB_MAX bilan bir xil. */
const SABAB_MAX = 500;

/**
 * AKKAUNTNI O'CHIRISH — OCHIQ sahifa (`/delete-account`, login talab qilmaydi).
 *
 * Google Play talabi: ilovadan tashqarida ham akkauntni o'chirishni so'rash yo'li bo'lishi
 * va unda ilova nomi, dasturchi, nima o'chishi/qolishi va muddat yozilishi shart. Shu manzil
 * Play Console'ga kiritiladi. Shaxs login + parol bilan tasdiqlanadi (`POST /public/delete-request`).
 *
 * So'rov `api` instansiyasi bilan EMAS, oddiy axios bilan yuboriladi: `api` interceptor'i
 * 401 da panel sessiyasini tozalab /login ga otib yuboradi — ochiq sahifada bu kerak emas.
 */
export default function DeleteAccount() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [yuborildi, setYuborildi] = useState(false);
  const [xato, setXato] = useState<string | null>(null);
  const [kutish, setKutish] = useState(false);

  const form = useForm({
    initialValues: { login: '', password: '', reason: '', confirm: false, website: '' },
    validate: {
      login: (v) => (v.trim() ? null : t('deleteAccount.required')),
      password: (v) => (v ? null : t('deleteAccount.required')),
      confirm: (v) => (v ? null : t('deleteAccount.confirmRequired')),
    },
  });

  const submit = form.onSubmit(async (v) => {
    setXato(null);
    setKutish(true);
    try {
      await axios.post(`${api.defaults.baseURL}/public/delete-request`, {
        login: v.login.trim(),
        password: v.password,
        reason: v.reason.trim() || undefined,
        website: v.website || undefined,
      });
      setYuborildi(true);
      form.reset();
    } catch (e) {
      const status = axios.isAxiosError(e) ? e.response?.status : undefined;
      setXato(
        status === 400 ? t('deleteAccount.errWrong')
          : status === 403 ? t('deleteAccount.errRole')
            : status === 429 ? t('deleteAccount.errLimit')
              : t('deleteAccount.errGeneric'),
      );
    } finally {
      setKutish(false);
    }
  });

  const app = { app: APP_NAME };
  const ochadi = t('deleteAccount.deletedItems', { returnObjects: true }) as string[];
  const qoladi = t('deleteAccount.keptItems', { returnObjects: true }) as string[];

  return (
    <Box mih="100vh" bg="var(--mantine-color-body)" py="xl">
      <Container size="md">
        <Group justify="space-between" mb="lg">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate(isAuthenticated ? '/' : '/login')}
          >
            {t('legal.back')}
          </Button>
          <LanguageMenu />
        </Group>

        <Paper p="xl" radius="md" withBorder>
          <Stack gap="xs" mb="md">
            <Group gap="sm" wrap="nowrap" align="flex-start">
              <IconUserX size={28} style={{ flexShrink: 0, marginTop: 4 }} />
              <Title order={2}>{t('deleteAccount.title', app)}</Title>
            </Group>
            <Badge variant="light" w="fit-content" component="a" href={DEVELOPER_URL} target="_blank" rel="noopener noreferrer" style={{ cursor: 'pointer' }}>
              {t('deleteAccount.developer', { dev: DEVELOPER_NAME })}
            </Badge>
          </Stack>
          <Text c="dimmed" mb="lg">{t('deleteAccount.intro', app)}</Text>
          <Divider mb="lg" />

          <Stack gap="lg">
            <Box>
              <Title order={4} mb={6}>
                <Group gap={8} component="span"><IconDeviceMobile size={18} />{t('deleteAccount.inAppTitle')}</Group>
              </Title>
              <Text size="sm" style={{ lineHeight: 1.65 }}>{t('deleteAccount.inAppSteps', app)}</Text>
            </Box>

            <Box>
              <Title order={4} mb={6}>
                <Group gap={8} component="span"><IconSend size={18} />{t('deleteAccount.formTitle')}</Group>
              </Title>
              {yuborildi ? (
                <Alert color="teal" variant="light" icon={<IconCircleCheck size={18} />} title={t('deleteAccount.successTitle')}>
                  {t('deleteAccount.successText')}
                </Alert>
              ) : (
                <form onSubmit={submit} noValidate>
                  <Stack gap="sm" maw={460}>
                    <Text size="sm" c="dimmed">{t('deleteAccount.formHint')}</Text>
                    <TextInput label={t('deleteAccount.login')} autoComplete="username" maxLength={100} {...form.getInputProps('login')} />
                    <PasswordInput label={t('deleteAccount.password')} autoComplete="current-password" maxLength={200} {...form.getInputProps('password')} />
                    <Textarea
                      label={t('deleteAccount.reason')}
                      placeholder={t('deleteAccount.reasonPlaceholder')}
                      autosize
                      minRows={2}
                      maxRows={5}
                      maxLength={SABAB_MAX}
                      {...form.getInputProps('reason')}
                    />
                    {/* Honeypot — odamga ko'rinmaydi, botlar to'ldiradi (backend jimgina e'tiborsiz qoldiradi) */}
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      style={{ position: 'absolute', left: -10000, width: 1, height: 1, opacity: 0 }}
                      value={form.values.website}
                      onChange={(e) => form.setFieldValue('website', e.currentTarget.value)}
                    />
                    <Checkbox label={t('deleteAccount.confirm')} {...form.getInputProps('confirm', { type: 'checkbox' })} />
                    {xato && <Alert color="red" variant="light">{xato}</Alert>}
                    <Group>
                      <Button type="submit" color="red" loading={kutish} leftSection={<IconTrash size={16} />}>
                        {t('deleteAccount.submit')}
                      </Button>
                    </Group>
                  </Stack>
                </form>
              )}
            </Box>

            <Box>
              <Title order={4} mb={6}>{t('deleteAccount.timingTitle')}</Title>
              <Text size="sm" style={{ lineHeight: 1.65 }}>{t('deleteAccount.timingText')}</Text>
            </Box>

            <Box>
              <Title order={4} mb={6}>{t('deleteAccount.deletedTitle')}</Title>
              <List size="sm" spacing={4}>
                {ochadi.map((x) => <List.Item key={x}>{x}</List.Item>)}
              </List>
            </Box>

            <Box>
              <Title order={4} mb={6}>{t('deleteAccount.keptTitle')}</Title>
              <List size="sm" spacing={4}>
                {qoladi.map((x) => <List.Item key={x}>{x}</List.Item>)}
              </List>
              <Text size="sm" c="dimmed" mt="sm">{t('deleteAccount.childNote')}</Text>
            </Box>

            <Box>
              <Title order={4} mb={6}>
                <Group gap={8} component="span"><IconMail size={18} />{t('deleteAccount.contactTitle')}</Group>
              </Title>
              <Text size="sm">
                {t('deleteAccount.contactText', { email: '\u0000' }).split('\u0000').map((part, i) => (
                  <span key={i}>
                    {i > 0 && <Anchor href={`mailto:${PRIVACY_CONTACT_EMAIL}`} inherit>{PRIVACY_CONTACT_EMAIL}</Anchor>}
                    {part}
                  </span>
                ))}
              </Text>
              <Anchor component={Link} to="/privacy" size="sm" mt="xs" display="inline-flex" style={{ alignItems: 'center', gap: 6 }}>
                <IconShieldLock size={14} />{t('deleteAccount.privacyLink')}
              </Anchor>
            </Box>
          </Stack>
        </Paper>

        <Text ta="center" size="xs" c="dimmed" mt="lg">
          © {new Date().getFullYear()} {DEVELOPER_NAME} · {APP_NAME}
        </Text>
      </Container>
    </Box>
  );
}
